import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import BackLink from "@/components/BackLink";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    Dialog,
} from "@/components/ui/dialog";
import {
    Calendar, Camera, IndianRupee, Loader2, Mail, Phone, Pencil, Scissors, UserCircle, Users,
} from "lucide-react";
import { toast } from "sonner";
import { PAY_TYPE_LABELS, SALON_PAY_MODELS } from "@/lib/payroll";
import { ROLE_LABELS } from "@/lib/permissions";
import { mediaUrl, userInitials } from "@/lib/media";
import { StaffDialog } from "@/pages/Staff";

const PHOTO_ACCEPT = "image/jpeg,image/png,image/webp";
const PHOTO_MAX_MB = 2;

const EYEBROW = "font-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs";
const GOLD = "#C9A227";
const CHOCOLATE = "#3D2817";
const MAROON = "#4A0E17";

const inr = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";

const fmtDateTime = (iso) =>
    iso
        ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
        : "—";

const ROLE_STYLES = {
    stylist: { bg: "#FDF3DC", text: "#9A7B10" },
    therapist: { bg: "#E0EDFA", text: "#3B82C4" },
    manager: { bg: "#FDE8EC", text: MAROON },
    receptionist: { bg: "#FBE4EF", text: "#D9468F" },
};

const navBtn =
    "border-[#E8E4DE] bg-white text-[#3D2817] shadow-sm hover:border-[#D4AF37]/45 hover:bg-[#FFFEF9]";

function RoleBadge({ role }) {
    const { bg, text } = ROLE_STYLES[role] ?? { bg: "#FAF9F6", text: CHOCOLATE };
    return (
        <span
            className="inline-block rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
            style={{ background: bg, color: text }}
        >
            {role}
        </span>
    );
}

function paySummary(staff, salonModel, payProfile, payroll) {
    const pt = salonModel === "fixed" ? "fixed" : salonModel === "commission" ? "commission" : (payProfile?.pay_type || staff?.pay_type || "commission");
    if (pt === "fixed") {
        const base = payProfile?.base_salary ?? staff?.base_salary ?? 0;
        const period = payProfile?.salary_period ?? staff?.salary_period ?? "monthly";
        return `${inr(base)} / ${period === "weekly" ? "week" : "month"}`;
    }
    if (pt === "hybrid") {
        const base = payProfile?.base_salary ?? staff?.base_salary ?? 0;
        const pct = payProfile?.commission_pct ?? staff?.commission_pct ?? 0;
        const thresh = payProfile?.commission_threshold ?? staff?.commission_threshold ?? 0;
        const parts = [];
        if (base) parts.push(`${inr(base)} base`);
        if (pct) parts.push(`${pct}% above ${inr(thresh)}`);
        return parts.join(" · ") || "Hybrid";
    }
    const pct = payProfile?.commission_pct ?? staff?.commission_pct ?? payroll?.commission_pct ?? 0;
    return `${pct}% of service revenue`;
}

function ProfileStat({ label, value, tone = "revenue" }) {
    const tones = {
        revenue: "#FDF3DC",
        bookings: "#E0EDFA",
        staff: "#FBE4EF",
    };
    const bg = tones[tone] ?? tones.revenue;
    return (
        <div
            className="relative overflow-hidden rounded-lg border border-[#E8E4DE]/80 px-3 py-2.5"
            style={{ background: `linear-gradient(150deg, ${bg}44 0%, #FFFFFF 55%)` }}
        >
            <p className="text-[9px] font-extrabold uppercase tracking-[0.12em]" style={{ color: CHOCOLATE }}>{label}</p>
            <p className="mt-1 truncate font-serif text-base font-extrabold" style={{ color: CHOCOLATE }} title={String(value)}>
                {value}
            </p>
        </div>
    );
}

function Section({ title, icon: Icon, children }) {
    return (
        <div className="rounded-xl border border-[#E8E4DE] bg-white p-4 shadow-[0_4px_22px_rgba(45,41,38,0.04)]">
            <h2 className="mb-3 flex items-center gap-2 text-xs font-extrabold uppercase tracking-wide text-[#8A8178]">
                <Icon className="size-3.5 text-[#D4AF37]" />
                {title}
            </h2>
            {children}
        </div>
    );
}

function Row({ left, right }) {
    return (
        <div className="flex items-center justify-between gap-3 border-b border-[#E8E4DE]/70 py-2.5 text-sm last:border-0">
            <span className="min-w-0 font-medium text-[#3D2817]">{left}</span>
            <span className="shrink-0 text-right">{right}</span>
        </div>
    );
}

function StaffProfilePhoto({ staff, onUpdated }) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const photoSrc = mediaUrl(staff.photo_url);
    const hasPhoto = !!photoSrc;

    const onPick = () => fileRef.current?.click();

    const onSelected = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!PHOTO_ACCEPT.split(",").includes(file.type)) {
            toast.error("Use JPG, PNG, or WebP");
            return;
        }
        if (file.size > PHOTO_MAX_MB * 1024 * 1024) {
            toast.error(`Image must be under ${PHOTO_MAX_MB} MB`);
            return;
        }
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post(`/staff/${staff.id}/photo`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onUpdated(data);
            toast.success("Profile photo updated");
        } catch (err) {
            const msg = err.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Could not upload photo");
        } finally {
            setUploading(false);
        }
    };

    const removePhoto = async () => {
        setUploading(true);
        try {
            const { data } = await api.delete(`/staff/${staff.id}/photo`);
            onUpdated(data);
            toast.success("Photo removed");
        } catch {
            toast.error("Could not remove photo");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="flex flex-col items-center gap-2 sm:items-start">
            <div className="relative">
                <Avatar
                    className="size-14 border-2 border-white shadow-sm ring-2 ring-[#E8E4DE]/80 sm:size-16"
                    style={!hasPhoto ? { background: staff.color || MAROON } : undefined}
                >
                    {hasPhoto && (
                        <AvatarImage
                            src={photoSrc}
                            alt={staff.full_name}
                            className="object-cover"
                        />
                    )}
                    <AvatarFallback
                        className="font-serif text-2xl font-bold text-white"
                        style={{ background: staff.color || MAROON }}
                    >
                        {userInitials(staff.full_name)}
                    </AvatarFallback>
                </Avatar>
                <button
                    type="button"
                    disabled={uploading}
                    onClick={onPick}
                    className="absolute -bottom-0.5 -right-0.5 flex size-7 items-center justify-center rounded-full border-2 border-white bg-[#4A0E17] text-white shadow-md transition-transform hover:scale-105 disabled:opacity-60"
                    aria-label="Upload staff photo"
                    data-testid="staff-upload-photo"
                >
                    {uploading ? (
                        <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                        <Camera className="size-3.5" />
                    )}
                </button>
                <input
                    ref={fileRef}
                    type="file"
                    accept={PHOTO_ACCEPT}
                    className="sr-only"
                    onChange={onSelected}
                />
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={uploading}
                    onClick={onPick}
                    className="h-7 border-[#E8E4DE] px-2.5 text-[11px] font-semibold"
                >
                    {hasPhoto ? "Change photo" : "Add photo"}
                </Button>
                {hasPhoto && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={uploading}
                        onClick={removePhoto}
                        className="h-7 px-2.5 text-[11px] font-semibold text-[#C84B31] hover:bg-[#FEF5F3] hover:text-[#A33923]"
                    >
                        Remove
                    </Button>
                )}
            </div>
            <p className="text-center text-[10px] font-medium text-[#9A9088] sm:text-left">
                JPG, PNG or WebP · max {PHOTO_MAX_MB} MB
            </p>
        </div>
    );
}

export default function StaffDetail() {
    const { staffId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [payrollDefaults, setPayrollDefaults] = useState({ salon_pay_model: "mixed" });
    const [editOpen, setEditOpen] = useState(false);

    const load = async () => {
        try {
            const [detailRes, payrollRes] = await Promise.all([
                api.get(`/staff/${staffId}`),
                api.get("/tenant/payroll").catch(() => ({ data: { salon_pay_model: "mixed" } })),
            ]);
            setData(detailRes.data);
            setPayrollDefaults(payrollRes.data);
        } catch {
            toast.error("Could not load staff profile");
            navigate("/app/staff", { replace: true });
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [staffId]);

    const onStaffUpdated = (updatedStaff) => {
        setData((prev) => (prev ? { ...prev, staff: { ...prev.staff, ...updatedStaff } } : prev));
    };

    if (!data) {
        return (
            <div className="p-6 lg:p-8">
                <BackLink to="/app/staff">Back to staff</BackLink>
                <p className="mt-8 text-sm font-medium text-[#8A8178]">Loading…</p>
            </div>
        );
    }

    const { staff, period, stats, payroll, pay_profile, salon_pay_model, services, login } = data;
    const salonModel = salon_pay_model || payrollDefaults.salon_pay_model || "mixed";
    const isActive = staff.active !== false;

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="staff-detail-page">
            <BackLink to="/app/staff">Back to staff</BackLink>

            <header className="relative overflow-hidden rounded-xl border border-[#E8E4DE] bg-white p-5 shadow-[0_4px_22px_rgba(45,41,38,0.04)] sm:p-6">
                <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start">
                        <StaffProfilePhoto staff={staff} onUpdated={onStaffUpdated} />
                        <div className="min-w-0 text-center sm:text-left">
                            <p className={EYEBROW} style={{ color: GOLD }}>Staff profile</p>
                            <h1 className="mt-1 font-serif text-2xl font-bold" style={{ color: CHOCOLATE }} data-testid="staff-detail-name">
                                {staff.full_name}
                            </h1>
                            <div className="mt-2 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                                <RoleBadge role={staff.role} />
                                <span
                                    className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                                        isActive ? "bg-[#E8F5EC] text-[#2D7A4F]" : "bg-[#FEF5F3] text-[#C84B31]"
                                    }`}
                                >
                                    {isActive ? "Active" : "Inactive"}
                                </span>
                            </div>
                            <div className="mt-3 space-y-1.5 text-sm font-medium text-[#8A8178]">
                                {staff.mobile && (
                                    <div className="flex items-center gap-2">
                                        <Phone className="size-3.5 text-[#D4AF37]" />
                                        {staff.mobile}
                                    </div>
                                )}
                                {staff.email && (
                                    <div className="flex items-center gap-2 truncate">
                                        <Mail className="size-3.5 shrink-0 text-[#D4AF37]" />
                                        <span className="truncate">{staff.email}</span>
                                    </div>
                                )}
                                <p className="text-xs">On team since {fmtDate(staff.created_at)}</p>
                            </div>
                        </div>
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-2">
                        <Button variant="outline" className={navBtn} asChild>
                            <Link to="/app/calendar">
                                <Calendar className="mr-1.5 size-4" />
                                Calendar
                            </Link>
                        </Button>
                        <Button
                            className="bg-[#4A0E17] font-semibold hover:bg-[#3a0b12]"
                            onClick={() => setEditOpen(true)}
                            data-testid="staff-detail-edit"
                        >
                            <Pencil className="mr-1.5 size-4" />
                            Edit
                        </Button>
                    </div>
                </div>
            </header>

            <div>
                <p className="mb-2 text-xs font-semibold text-[#8A8178]">
                    This month ({fmtDate(period.from)} – {fmtDate(period.to)})
                </p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <ProfileStat label="Revenue" value={inr(stats.revenue)} tone="revenue" />
                    <ProfileStat label="Earnings" value={inr(stats.earnings)} tone="staff" />
                    <ProfileStat label="Appointments" value={stats.appointments} tone="bookings" />
                    <ProfileStat label="Completed" value={stats.completed} tone="bookings" />
                </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
                <Section title="Pay" icon={IndianRupee}>
                    <p className="mb-3 text-sm font-semibold" style={{ color: CHOCOLATE }}>
                        {paySummary(staff, salonModel, pay_profile, payroll)}
                    </p>
                    <div className="rounded-lg border border-[#E8E4DE]/80 bg-[#FFFEF9]/60 px-3 py-2 text-xs text-[#8A8178]">
                        <span className="font-bold text-[#5C534C]">
                            {PAY_TYPE_LABELS[payroll.pay_type] || payroll.pay_type}
                        </span>
                        {salonModel !== "mixed" && (
                            <span> · Salon uses {SALON_PAY_MODELS[salonModel]?.short || salonModel} pay</span>
                        )}
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                        <div className="rounded-lg bg-[#FAF9F6] px-2 py-2">
                            <p className="font-extrabold uppercase tracking-wide text-[#8A8178]">Revenue</p>
                            <p className="mt-1 font-bold tabular-nums" style={{ color: CHOCOLATE }}>{inr(payroll.revenue)}</p>
                        </div>
                        <div className="rounded-lg bg-[#FAF9F6] px-2 py-2">
                            <p className="font-extrabold uppercase tracking-wide text-[#8A8178]">Commission</p>
                            <p className="mt-1 font-bold tabular-nums" style={{ color: GOLD }}>{inr(payroll.commission)}</p>
                        </div>
                        <div className="rounded-lg bg-[#FAF9F6] px-2 py-2">
                            <p className="font-extrabold uppercase tracking-wide text-[#8A8178]">Total pay</p>
                            <p className="mt-1 font-bold tabular-nums" style={{ color: MAROON }}>{inr(payroll.total_earnings)}</p>
                        </div>
                    </div>
                </Section>

                <Section title="App login" icon={UserCircle}>
                    {!login ? (
                        <p className="text-sm text-[#8A8178]">
                            No login linked. Invite from{" "}
                            <Link to="/app/settings" className="font-bold underline" style={{ color: MAROON }}>
                                Settings → Team access
                            </Link>
                            .
                        </p>
                    ) : (
                        <div className="space-y-1 text-sm">
                            <p className="font-semibold" style={{ color: CHOCOLATE }}>{login.email}</p>
                            <p className="text-[#8A8178]">{ROLE_LABELS[login.role] || login.role}</p>
                            <span
                                className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${
                                    login.active
                                        ? "bg-[#E8F5EC] text-[#2D7A4F]"
                                        : login.invited
                                          ? "bg-[#FDF3DC] text-[#9A7B10]"
                                          : "bg-[#FEF5F3] text-[#C84B31]"
                                }`}
                            >
                                {login.active ? "Active" : login.invited ? "Pending invite" : "Inactive"}
                            </span>
                        </div>
                    )}
                </Section>
            </div>

            {services.length > 0 && (
                <Section title="Services" icon={Scissors}>
                    <div className="flex flex-wrap gap-2">
                        {services.map((s) => (
                            <span
                                key={s.id}
                                className="rounded-full border border-[#E8E4DE] bg-[#FFFEF9] px-3 py-1 text-xs font-semibold text-[#5C534C]"
                            >
                                {s.name}
                            </span>
                        ))}
                    </div>
                </Section>
            )}

            <div className="grid gap-4 lg:grid-cols-2">
                <Section title="Upcoming" icon={Calendar}>
                    {data.appointments_upcoming.length === 0 ? (
                        <p className="text-sm text-[#8A8178]">No upcoming bookings.</p>
                    ) : (
                        data.appointments_upcoming.map((a) => (
                            <Row
                                key={a.id}
                                left={`${fmtDateTime(a.start_at)} · ${a.service_name}`}
                                right={<span className="text-xs font-bold capitalize text-[#8A8178]">{a.status}</span>}
                            />
                        ))
                    )}
                </Section>

                <Section title="Recent bookings" icon={Users}>
                    {data.appointments_recent.length === 0 ? (
                        <p className="text-sm text-[#8A8178]">No past bookings yet.</p>
                    ) : (
                        data.appointments_recent.map((a) => (
                            <Row
                                key={a.id}
                                left={`${a.customer_name} · ${a.service_name}`}
                                right={
                                    <span className="text-xs text-[#8A8178]">
                                        {fmtDate(a.start_at)}
                                    </span>
                                }
                            />
                        ))
                    )}
                </Section>
            </div>

            <Section title="Recent sales" icon={IndianRupee}>
                {data.sales_recent.length === 0 ? (
                    <p className="text-sm text-[#8A8178]">No paid sales this month.</p>
                ) : (
                    data.sales_recent.map((s, i) => (
                        <Row
                            key={`${s.invoice_id}-${i}`}
                            left={`${s.invoice_number || "Receipt"} · ${s.item_name}`}
                            right={<span className="font-bold tabular-nums" style={{ color: CHOCOLATE }}>{inr(s.amount)}</span>}
                        />
                    ))
                )}
            </Section>

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                {editOpen && (
                    <StaffDialog
                        staff={staff}
                        salonModel={salonModel}
                        payrollDefaults={payrollDefaults}
                        onClose={() => setEditOpen(false)}
                        onSaved={() => { setEditOpen(false); load(); }}
                    />
                )}
            </Dialog>
        </div>
    );
}
